import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';

import PaddedDivider from 'components/PaddedDivider';

import ArrowRightIcon from 'mdi-material-ui/ArrowRight';

const PublicationCard = styled(Card)`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  transition: all 0.15s ease-in-out;

  &:hover {
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.15);
  }
`;

const SpacedCardActions = styled(CardActions)`
  justify-content: space-between;
`;

const PublicationProducts = styled.div`
  margin: 0.5rem 0;
`;

const ProductLink = styled(Link)`
  display: inline-block;
`;

const ProductImg = styled.img`
  width: 30px;
  margin-right: 1rem;
  filter: grayscale(100%);
  opacity: 0.75;
  transition: all 0.15s ease-in-out;

  ${PublicationCard}:hover & {
    filter: none;
    opacity: 1;
  }
`;

const JournalTypography = styled(Typography)`
  margin-bottom: 1.5rem;
`;

const AffiliationTypography = styled(Typography)`
  font-weight: bold;
`;

const AuthorsTypography = styled(Typography)`
  font-style: italic;
  margin-top: 1rem;
`;

const Pipe = styled.span`
  width: 1px;
  background: rgba(0, 0, 0, 0.12);
  display: inline-block;
  height: 1.25em;
  margin: 0 0.75em -0.25em 0.75em;
`;

class PublicationItem extends React.PureComponent {
  render() {
    const { publication } = this.props;

    return (
      <PublicationCard>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {publication.title}
          </Typography>
          <JournalTypography variant="overline" gutterBottom>
            {publication.journal}, {dayjs(publication.date).format('MMMM YYYY')}
          </JournalTypography>
          <Typography variant="body1" gutterBottom>
            {publication.summary}
          </Typography>
          <AuthorsTypography variant="body1" gutterBottom>
            {publication.authors}
          </AuthorsTypography>
          <AffiliationTypography variant="body1" gutterBottom>
            {publication.affiliation}
          </AffiliationTypography>
          <PaddedDivider spacing={1.5} />
          {publication.applications.length > 0 && (
            <Typography variant="body1" gutterBottom inline>
              <strong>Applications: </strong>
              {publication.applications.map(p => ` ${p.title}`).toString()}
            </Typography>
          )}
          {publication.applications.length > 0 &&
            publication.research_areas.length > 0 && <Pipe />}

          {publication.research_areas.length > 0 && (
            <Typography variant="body1" gutterBottom inline>
              <strong>Research Areas: </strong>
              {publication.research_areas.map(ra => ` ${ra.title}`).toString()}
            </Typography>
          )}
        </CardContent>

        <SpacedCardActions>
          <Button color="primary" href={publication.link.href} target="_blank">
            {publication.link.title} <ArrowRightIcon />
          </Button>
          <PublicationProducts>
            {publication.products.map((product, idx) => (
              <Tooltip
                title={product.product_header_cta.product_name}
                placement="top"
                key={idx}
              >
                <ProductLink to={product.url}>
                  <ProductImg src={product.product_header_cta.icon.url} />
                </ProductLink>
              </Tooltip>
            ))}
          </PublicationProducts>
        </SpacedCardActions>
      </PublicationCard>
    );
  }
}

PublicationItem.propTypes = {
  publication: PropTypes.object.isRequired
};

export default PublicationItem;
